import React, { useMemo } from "react";
import { useQuery } from "react-query";
import { useLocation } from "react-router";
import { Form } from "react-final-form";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Divider from "@material-ui/core/Divider";
import queryString from "querystring";
import Loading from "../../../components/Loading";
import Error from "../../../components/Error";
import theme from "../../../themes";
import PublicLayout from "../../app/components/layouts/PublicLayout";
import useUserQueries from "../../user/hooks/useUserQueries";
import EmailPreferencesForm from "./EmailPreferencesForm";
import { emailPreferencesConfigs } from "./constants";
import {
  EmailType,
  IEmailPreferences,
  IUnsubscription,
  TEmailPreferenceRecord,
} from "./types";
import { useNotify } from "../../notify/useNotify";
import { NotifyMode } from "../../notify/NotifyModeEnum";
import { UserRole } from "../../user/enums/UserRole";

const useStyles = makeStyles(() => ({
  headerTitle: {
    fontSize: 43,
    marginBottom: 16,
    lineHeight: "54px",
    fontWeight: "bold",
    color: theme.palette.getContrastText(theme.palette.primary.dark),
  },
  bodyTitle: {
    marginTop: 16,
    marginBottom: 8,
    fontSize: 24,
  },
  bodyContainer: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    backgroundColor: theme.palette.background.default,
    maxWidth: "100%",
    minHeight: "calc(100vh - 170px)",
  },
  formContainer: {
    maxWidth: 700,
    width: "100%",
  },
  boxCaption: {
    marginTop: 32,
    color: "gray",
  },
  divider: {
    marginTop: 24,
    marginBottom: 8,
  },
}));

export default function EmailPreferences() {
  const classes = useStyles();
  const snackNotify = useNotify();

  const { fetchTokenEmailPreferences, updateTokenEmailPreferences } =
    useUserQueries();
  const location = useLocation();

  const queryParams = queryString.parse(location.search.split("?").join(""));
  const token: string = queryParams.code as string;

  const { isLoading, isError, refetch, data } = useQuery<{
    data: IEmailPreferences;
  }>(
    `emailPreferences${token?.slice(5)}`,
    () => fetchTokenEmailPreferences(token),
    { enabled: token },
  );

  const initialData = useMemo<{
    role?: UserRole;
    preferences: TEmailPreferenceRecord;
  }>(() => {
    if (!data?.data?.unsubscriptions)
      return {} as { role?: UserRole; preferences: TEmailPreferenceRecord };

    const preferences = emailPreferencesConfigs[data.data.role].reduce(
      (acc, item) => {
        acc[item.type] = item.defaultValue;

        return acc;
      },
      {},
    );

    // applying received data
    data.data.unsubscriptions.forEach((item: IUnsubscription) => {
      // set false if not "All", otherwise set to true
      preferences[item.type] = item.type === EmailType.All;
    });

    return {
      role: data?.data.role,
      preferences: preferences,
    };
  }, [data?.data]);

  async function _submit(data: {
    role: UserRole;
    preferences: TEmailPreferenceRecord;
  }) {
    const unsubscriptions = Object.entries(data.preferences).reduce(
      (acc, [key, value]) => {
        if (
          (!value && key !== EmailType.All) ||
          (value && key === EmailType.All)
        ) {
          acc.push({ type: key as EmailType });
        }

        return acc;
      },
      [] as IUnsubscription[],
    );

    const {
      data: { message },
    } = await updateTokenEmailPreferences(token, unsubscriptions);
    await refetch();
    snackNotify("Success", message, NotifyMode.SUCCESS);
  }

  if (!token || isError) {
    return <Error text={"Invalid token."}></Error>;
  }

  return (
    <PublicLayout>
      {isLoading && <Loading />}
      <Container>
        <Typography component={"h1"} className={classes.headerTitle}>
          Let's Keep In Touch
        </Typography>
      </Container>
      <Container className={classes.bodyContainer}>
        <Typography className={classes.bodyTitle}>
          Please adjust your email preferences below, and we will refine our
          emails accordingly.
        </Typography>
        <Container className={classes.formContainer}>
          <Typography className={classes.boxCaption}>
            Preferences for {data?.data.email}
          </Typography>
          <Divider className={classes.divider} />
          <Form
            onSubmit={_submit}
            initialValues={initialData}
            component={EmailPreferencesForm}
          />
        </Container>
      </Container>
    </PublicLayout>
  );
}
